import { Document, View } from '@react-pdf/renderer';
import { getTaxValue, getTotalValue } from '../../../pricing';
import { en } from '../../theme/languages/en';
import { BillingAccountDetails, PricingModelTab, Supplier } from '../../../common';
import {
  EntityBankData,
  PageComponent,
  Span,
  SummaryData,
  SummaryProductTable,
  SummaryTable,
  UsageLogs,
  CryptoPaymentData,
} from '../../components';
import { getBankData, sum } from '../../utils/general';
import { invoiceSummaryValuesList } from '../../utils/invoiceSummary';
import { isUsageLogsExist } from '../../utils/usageUtils';
import { Payout } from '../../../common/interfaces/payout.interface';
import { getCreditNoteIdName } from '../../utils';
import { PayoutMetaData } from '../../components/PayoutMetaData/PayoutMetaData';
import { styles } from './PayoutPDF.module';

interface PayoutPDFProps {
  payoutData: Payout;
  supplier?: Supplier;
  bankDetails?: BillingAccountDetails;
  isPreview?: boolean;
}

export const PayoutPDF = ({ payoutData, supplier, bankDetails, isPreview }: PayoutPDFProps) => {
  const taxValue = sum(
    payoutData?.pricingModel?.tabs.map((tab: PricingModelTab) => getTaxValue(tab?.subTotal, 0, tab?.tax)),
  );
  const totalValue = sum(
    payoutData?.pricingModel?.tabs.map((tab: PricingModelTab) => getTotalValue(tab?.subTotal, 0, tab?.tax)),
  );

  const subTotalValue = sum(payoutData?.pricingModel?.tabs.map((tab: PricingModelTab) => tab?.subTotal));

  return (
    <Document>
      <PageComponent supplier={supplier || payoutData.supplier} isPreview={isPreview} documentState={payoutData?.state}>
        <View style={styles.container}>
          <PayoutMetaData
            title={`${en.payout} ${getCreditNoteIdName(payoutData)}`}
            payoutData={payoutData}
            showBillingPeriod={payoutData?.pricingModel?.tabs.length == 1}
          />

          {payoutData?.pricingModel?.tabs.length > 1 ? (
            <SummaryTable products={payoutData?.pricingModel?.tabs} periodColumnName={en.billingPeriod} />
          ) : (
            <SummaryProductTable
              product={payoutData?.pricingModel?.tabs[0]}
              isPartialPeriod={payoutData?.pricingModel?.tabs[0]?.isPartialPeriod}
              style={styles.productTable}
              showOnlyTable
            />
          )}

          <SummaryData
            summaryItems={invoiceSummaryValuesList({
              currency: payoutData?.pricingModel?.tabs[0].currency,

              subTotalCalculatedValue: subTotalValue,
              taxCalculatedValue: taxValue,

              hidePercentage: payoutData?.pricingModel?.tabs.length > 1,

              taxPercentage:
                payoutData?.pricingModel?.tabs.length == 1 ? payoutData?.pricingModel?.tabs[0].tax : undefined,

              total: totalValue,
              totalDue: totalValue,
              isPartialPeriod: payoutData?.pricingModel?.tabs.some((tab) => tab.isPartialPeriod),
            })}
          />

          {payoutData?.pricingModel?.tabs?.length == 1 && (
            <View style={styles.noteContainer} wrap={false}>
              <Span style={styles.note} canBeMissingValue missingValue={payoutData?.pricingModel?.tabs[0]?.note}>
                {en.note}
              </Span>
              <Span style={styles.text} canBeMissingValue missingValue={payoutData?.pricingModel?.tabs[0]?.note}>
                {payoutData?.pricingModel?.tabs[0]?.note}
              </Span>
            </View>
          )}

          <View style={styles.bottomContainer}>
            {!!bankDetails?.bank && <EntityBankData title={en.paymentDetails} data={getBankData(bankDetails)} />}

            {payoutData?.cryptoSettings?.isEnabled && <CryptoPaymentData data={payoutData?.cryptoSettings} />}
          </View>
        </View>
      </PageComponent>

      {payoutData?.pricingModel?.tabs.length > 1 && (
        <PageComponent
          supplier={supplier || payoutData.supplier}
          isPreview={isPreview}
          documentState={payoutData?.state}
        >
          <View style={styles.productTablesContainer}>
            {payoutData?.pricingModel.tabs.map((tab, index) => (
              <SummaryProductTable key={index} product={tab} isPartialPeriod={tab?.isPartialPeriod} />
            ))}
          </View>
        </PageComponent>
      )}

      {isUsageLogsExist(payoutData?.usageReportsByProduct) && payoutData.customer?.settings?.attachUsageLogsPdf && (
        <UsageLogs
          usageReportsByProduct={payoutData?.usageReportsByProduct}
          poNumber={payoutData?.poNumber}
          isPreview={isPreview}
          supplier={supplier}
        />
      )}
    </Document>
  );
};
